export interface ApiErrorBody { error?: string }

export class ApiClient {
  private readonly baseUrl: string;
  private readonly transport: typeof fetch;

  constructor(
    baseUrl = "",
    transport: typeof fetch = globalThis.fetch.bind(globalThis),
  ) {
    this.baseUrl = baseUrl;
    this.transport = transport;
  }

  response(path: string, init: RequestInit = {}): Promise<Response> {
    return this.transport(this.url(path), { credentials: "include", ...init });
  }

  async json<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await this.response(path, init);
    if (!response.ok) throw new ApiResponseError(response.status, await this.error(response, "Request failed"));
    return response.json() as Promise<T>;
  }

  async optional<T>(path: string, init: RequestInit = {}): Promise<T | null> {
    try {
      const response = await this.response(path, init);
      return response.ok ? await response.json() as T : null;
    } catch {
      return null;
    }
  }

  jsonRequest(method: "POST" | "PATCH" | "PUT" | "DELETE", body?: unknown): RequestInit {
    return {
      method,
      headers: body === undefined ? undefined : { "content-type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    };
  }

  async error(response: Response, fallback: string): Promise<string> {
    return (await response.json().catch(() => ({})) as ApiErrorBody).error || fallback;
  }

  private url(path: string): string {
    if (!this.baseUrl || /^https?:\/\//.test(path)) return path;
    return new URL(path, this.baseUrl).toString();
  }
}

export class ApiResponseError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiResponseError";
    this.status = status;
  }
}

export const apiClient = new ApiClient();
